import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import type { SortPayload } from 'react-admin';
import {
  Link as ReactAdminLink,
  ShowButton,
  useCreatePath,
  useGetList,
} from 'react-admin';

import { nameColumnStyling } from '../../../utils/nameFieldSx';

export type ExerciseTreeResourceName =
  | 'Exercise'
  | 'ExerciseTypeCategoryGroup'
  | 'ExerciseTypeCategory'
  | 'ExerciseType'
  | 'ExerciseBase';

export type ChildrenTableColumn = {
  source: string;
  label: string;
};

export type ChildrenTableSectionProps = {
  label: string;
  count: number;
  resource: ExerciseTreeResourceName;
  filter: Record<string, string>;
  columns: ChildrenTableColumn[];
  perPage?: number;
  sort?: SortPayload;
};

export const DEFAULT_CHILDREN_PER_PAGE = 50;

type ChildCountLinkProps = {
  label: string;
  count: number;
  resource: ExerciseTreeResourceName;
  filter: Record<string, string>;
};

const ChildCountLink: React.FC<ChildCountLinkProps> = ({
  label,
  count,
  resource,
  filter,
}) => {
  const createPath = useCreatePath();
  const to = React.useMemo(() => {
    const basePath = createPath({ resource, type: 'list' });
    const params = new URLSearchParams();
    params.set('filter', JSON.stringify(filter));
    return `${basePath}?${params.toString()}`;
  }, [createPath, filter, resource]);

  return (
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {label}
      <Box
        component="span"
        sx={{
          ml: 1,
          '& a': {
            color: 'primary.main',
            textDecoration: 'underline',
          },
        }}
      >
        <ReactAdminLink to={to}>({count})</ReactAdminLink>
      </Box>
    </Typography>
  );
};

function getRecordValue(
  record: Record<string, unknown>,
  source: string,
): React.ReactNode {
  const parts = source.split('.');
  let value: unknown = record;
  for (const part of parts) {
    value =
      value != null && typeof value === 'object' && part in value
        ? (value as Record<string, unknown>)[part]
        : undefined;
  }
  if (value == null) {
    return '-';
  }
  if (typeof value === 'object' && value !== null && 'name' in value) {
    return (value as { name: string }).name;
  }
  return String(value);
}

export const ChildrenTableSection: React.FC<ChildrenTableSectionProps> = ({
  label,
  count,
  resource,
  filter,
  columns,
  perPage = DEFAULT_CHILDREN_PER_PAGE,
  sort = { field: 'name', order: 'ASC' },
}) => {
  const [page, setPage] = React.useState(0);

  const { data, total, isLoading } = useGetList(resource, {
    filter,
    pagination: { page: page + 1, perPage },
    sort,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <ChildCountLink
        label={label}
        count={count}
        resource={resource}
        filter={filter}
      />
      {(isLoading || (total ?? 0) > 0) && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.source}
                    sx={
                      col.source === 'name' || col.source === 'displayName'
                        ? nameColumnStyling
                        : undefined
                    }
                  >
                    {col.label}
                  </TableCell>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>Loading...</TableCell>
                </TableRow>
              ) : (
                (data ?? []).map((record: Record<string, unknown>) => (
                  <TableRow key={String(record.id)}>
                    {columns.map((col) => (
                      <TableCell
                        key={col.source}
                        sx={
                          col.source === 'name' || col.source === 'displayName'
                            ? nameColumnStyling
                            : undefined
                        }
                      >
                        {getRecordValue(record, col.source)}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <ShowButton
                        resource={resource}
                        record={record as { id: string }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total ?? 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={perPage}
            rowsPerPageOptions={[perPage]}
          />
        </TableContainer>
      )}
    </Box>
  );
};
